export type RgbColor = { r: number; g: number; b: number };

// Parses #rgb, #rrggbb and rgb()/rgba() strings into {r,g,b}
export const parseColor = (color: string): RgbColor | null => {
  const hex = color.trim().match(/^#([\da-f]{3,8})$/i)?.[1];
  if (hex) {
    if (hex.length === 3 || hex.length === 4) {
      const [r, g, b] = hex.split('');
      return {
        r: parseInt(r + r, 16),
        g: parseInt(g + g, 16),
        b: parseInt(b + b, 16),
      };
    }
    if (hex.length === 6 || hex.length === 8) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }
    return null;
  }

  const rgbMatch = color
    .trim()
    .match(
      /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/i
    );
  if (rgbMatch) {
    const [, r, g, b] = rgbMatch;
    return {
      r: Number(r),
      g: Number(g),
      b: Number(b),
    };
  }

  return null;
};



// WCAG relative luminance
export const relativeLuminance = ({
  r,
  g,
  b,
}: {
  r: number;
  g: number;
  b: number;
}): number => {
  const toLinear = (channel: number) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return (
    0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
  );
};

// WCAG contrast ratio between two relative luminances, 1 (no contrast) to 21 (max)
export const contrastRatio = (luminanceA: number, luminanceB: number) => {
  const lighter = Math.max(luminanceA, luminanceB);
  const darker = Math.min(luminanceA, luminanceB);
  return (lighter + 0.05) / (darker + 0.05);
};

// Picks black or white text, whichever gives the higher WCAG contrast ratio.
export const getTextColor = (bgColor: string | null): string => {
  if (typeof bgColor !== 'string') {
    return '#000000';
  }
  const bg = parseColor(bgColor);
  if (!bg) {
    return '#000000';
  }

  const bgLuminance = relativeLuminance(bg);

  const blackContrast = contrastRatio(bgLuminance, relativeLuminance({ r: 0, g: 0, b: 0 }));
  const whiteContrast = contrastRatio(bgLuminance, relativeLuminance({ r: 255, g: 255, b: 255 }));

  return blackContrast >= whiteContrast ? '#000000' : '#ffffff';
};